import './Checkbox.scss';
import { InputHTMLAttributes, Ref, forwardRef } from 'react';

/**
 * @description Interface extending standard input HTML attributes for the Checkbox component
 * @interface CheckboxProps
 * @extends {InputHTMLAttributes<HTMLInputElement>}
 */
interface CheckboxProps extends InputHTMLAttributes<HTMLInputElement> {
  /**
   * @description Additional CSS class name for styling
   */
  classname?: string;
}

/**
 * @description Custom checkbox component with SVG styling.
 * Provides a visually enhanced checkbox with custom styling and label support.
 *
 * @SADtrace SAD-003-3.1, SAD-003-4.1, SAD-003-5.1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @component
 * @param {CheckboxProps} props - Component props
 * @param {string} [props.id] - Unique identifier for the checkbox
 * @param {string} [props.title] - Text label to display next to the checkbox
 * @param {string} [props.value] - Value of the checkbox
 * @param {Function} [props.onChange] - Function to call when checkbox state changes
 * @param {boolean} [props.checked] - Whether the checkbox is checked
 * @param {boolean} [props.disabled] - Whether the checkbox is disabled
 * @param {Function} [props.onBlur] - Function to call when checkbox loses focus
 * @param {string} [props.classname] - Additional CSS class name for styling
 * @param {string} [props.name] - Name attribute for the checkbox
 * @param {Ref<HTMLInputElement>} ref - Forwarded ref to access the input element
 *
 * @returns {JSX.Element} Rendered checkbox component
 */
// eslint-disable-next-line react/display-name
const Checkbox = forwardRef(
  (
    {
      id,
      title,
      value,
      onChange,
      checked,
      disabled,
      onBlur,
      classname,
      name,
      ...rest
    }: CheckboxProps,
    ref: Ref<HTMLInputElement>,
  ) => {
    return (
      <label
        htmlFor={id}
        className={'label-cbx ' + (classname ? classname : '')}
      >
        <input
          id={id}
          type="checkbox"
          className="label-cbx-invisible"
          value={value}
          onChange={onChange}
          checked={checked}
          disabled={disabled}
          onBlur={onBlur}
          ref={ref}
          name={name}
          {...rest}
        />
        <div className="checkbox">
          <svg width="20px" height="20px" viewBox="0 0 20 20">
            <path d="M3,1 L17,1 L17,1 C18.1045695,1 19,1.8954305 19,3 L19,17 L19,17 C19,18.1045695 18.1045695,19 17,19 L3,19 L3,19 C1.8954305,19 1,18.1045695 1,17 L1,3 L1,3 C1,1.8954305 1.8954305,1 3,1 Z"></path>
            <polyline points="4 11 8 15 16 6"></polyline>
          </svg>
        </div>
        {title && <span>{title}</span>}
      </label>
    );
  },
);

export default Checkbox;
